import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Search } from "lucide-react";
import api from "@/lib/api";

export default function SearchResults() {
  const [sp] = useSearchParams();
  const q = (sp.get("q") || "").trim();
  const { data, isLoading } = useQuery({
    queryKey: ["search", q],
    queryFn: async () => (await api.get(`/search?q=${encodeURIComponent(q)}&limit=50`)).data,
    enabled: q.length >= 2,
  });
  const news = data?.news || [];
  const blogs = data?.blogs || [];
  const ipos = data?.ipos || [];
  const total = news.length + blogs.length + ipos.length;

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 max-w-[1200px] mx-auto">
      <Helmet>
        <title>{q ? `Search: ${q} · Nivesh Street` : "Search · Nivesh Street"}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="ns-eyebrow mb-2">Search</div>
      <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#1A1A1A] mb-2 flex items-center gap-3">
        <Search className="w-7 h-7 text-[#D91E2A]" />
        {q ? `Results for "${q}"` : "What are you looking for?"}
      </h1>
      {q && !isLoading && (
        <p className="text-[#6B6C6F] text-sm mb-8">{total} match{total === 1 ? "" : "es"} across news, blogs & IPOs.</p>
      )}
      {q.length < 2 && <p className="text-[#6B6C6F] text-sm">Type at least 2 characters.</p>}
      {isLoading && <p className="text-sm text-[#8C8D90] font-mono-fin">Searching…</p>}

      {news.length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading text-xl text-[#1A1A1A] mb-3 flex items-center"><span className="ns-rule" />News</h2>
          <div className="ns-card divide-y divide-[#E2DDD3]">
            {news.map(n => (
              <Link key={n.slug} to={`/news/${n.slug}`} className="flex gap-4 px-5 py-4 hover:bg-[#FAF8F3] group" data-testid={`result-news-${n.slug}`}>
                {n.image && <img src={n.image} alt="" className="w-20 h-20 object-cover rounded flex-shrink-0" loading="lazy" />}
                <div className="flex-1 min-w-0">
                  <div className="ns-eyebrow mb-1">{n.source} · {n.category}</div>
                  <div className="font-heading text-base font-bold text-[#1A1A1A] group-hover:text-[#D91E2A] leading-snug line-clamp-2">{n.title}</div>
                  {n.summary && <div className="text-xs text-[#6B6C6F] mt-1 line-clamp-2">{n.summary.replace(/<[^>]*>/g, "")}</div>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {blogs.length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading text-xl text-[#1A1A1A] mb-3 flex items-center"><span className="ns-rule" />Blogs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {blogs.map(b => (
              <Link key={b.slug} to={`/blogs/${b.slug}`} className="ns-card p-5 hover:border-[#D91E2A]/40 transition-colors" data-testid={`result-blog-${b.slug}`}>
                <div className="ns-eyebrow mb-2">{b.category}</div>
                <div className="font-heading text-lg font-bold text-[#1A1A1A] leading-snug">{b.title}</div>
                {b.excerpt && <div className="text-xs text-[#6B6C6F] mt-2 line-clamp-2">{b.excerpt}</div>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {ipos.length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading text-xl text-[#1A1A1A] mb-3 flex items-center"><span className="ns-rule" />IPOs</h2>
          <div className="ns-card divide-y divide-[#E2DDD3]">
            {ipos.map(i => (
              <Link key={i.id} to="/ipos" className="flex items-center justify-between px-5 py-4 hover:bg-[#FAF8F3]" data-testid={`result-ipo-${i.id}`}>
                <div>
                  <div className="font-semibold text-[#1A1A1A]">{i.company_name}</div>
                  <div className="text-xs text-[#6B6C6F]">{i.price_band} · {i.open_date}</div>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#D91E2A]">{i.status}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!isLoading && q.length >= 2 && total === 0 && (
        <div className="ns-card p-10 text-center text-sm text-[#6B6C6F] font-mono-fin">
          No matches. Try a company name, ticker or topic.
        </div>
      )}
    </div>
  );
}
